import { Tooltip } from '@systemeio/ui'
import React from 'react'
import { InstanceWithNestedIdType } from 'shared/components/table/types/instance-interface'

interface TableDataInterface<T> {
  items: T[]
  [key: string]: any
}
type AlignType = 'justify-end' | 'justify-start' | 'justify-center'
export interface TabColumnDefinitionInterface<T, K> {
  key: keyof T | T[keyof T]
  header: string | JSX.Element
  onHeaderRender?: (data?: K) => JSX.Element
  subHeaders: {
    widthPercent?: number
    title: string | JSX.Element
    onRender?: (instance: T) => JSX.Element
    onRenderSmall?: (instance: T) => JSX.Element
    align?: AlignType
  }[]
  subHeaderWidthPercent?: number[]
  widthPercent?: number
  shouldRender?: boolean
}

export interface TableProps<T, K> {
  columns: TabColumnDefinitionInterface<T, K>[]
  data?: K
  passDataToHeader?: boolean
}

function TabTable<T extends { id: number | string }, K extends TableDataInterface<T>>({
  columns,
  data,
  passDataToHeader,
}: TableProps<T, K>) {
  const totalWidth = columns
    .filter(c => c.widthPercent !== undefined)
    .reduce((prev, cur) => prev + (cur.widthPercent || 25), 0)

  const rightBorderStyles = data ? '' : 'last:lg:border-r'

  return (
    <div
      className={`flex w-full flex-col lg:border-t ${
        data ? 'lg:border-r' : ''
      } lg:border-text-primary/15 text-text-primary/60 lg:text-text-primary/100 lg:text-sm`}
    >
      <TableHeader
        columns={columns}
        totalWidth={totalWidth}
        rightBorderStyles={rightBorderStyles}
        data={passDataToHeader ? data : undefined}
      />
      <TableSubHeader
        columns={columns}
        totalWidth={totalWidth}
        rightBorderStyles={rightBorderStyles}
      />
      <div className="overflow-auto">
        <TableRows columns={columns} data={data} totalWidth={totalWidth} />
      </div>
    </div>
  )
}

function TableHeader<T extends InstanceWithNestedIdType<T>, K extends TableDataInterface<T>>({
  columns,
  totalWidth,
  rightBorderStyles,
  data,
}: {
  columns: TabColumnDefinitionInterface<T, K>[]
  totalWidth: number
  rightBorderStyles: string
  data?: K
}) {
  const headers = columns.map((column, index) => {
    const widthPercent = column.widthPercent || 25
    const onRender = column.onHeaderRender && data ? column.onHeaderRender(data) : column.header

    return (
      <div
        key={`header-cell-${index}`}
        className={`lg:border-text-primary/15 text-text-primary font-inter hidden truncate text-sm font-bold lg:table-cell lg:border-b lg:border-l lg:text-[15px] ${rightBorderStyles}`}
        style={{
          flex: `${(widthPercent / totalWidth) * 100}`,
        }}
      >
        {typeof onRender === 'string' ? (
          <Tooltip
            mode="overflow"
            label={column.header}
            wrapperClassName={'justify-center'}
            className="flex h-14 w-full items-center justify-center px-3"
          >
            <span className="inline-block w-full truncate">{onRender}</span>
          </Tooltip>
        ) : (
          <div className="flex h-14 w-full min-w-0 items-center justify-center truncate">
            <div className="truncate">{onRender}</div>
          </div>
        )}
      </div>
    )
  })

  return <div className="bg-background-primary/40 flex items-center">{headers}</div>
}

function TableSubHeader<T extends InstanceWithNestedIdType<T>, K extends TableDataInterface<T>>({
  columns,
  totalWidth,
  rightBorderStyles,
}: {
  columns: TabColumnDefinitionInterface<T, K>[]
  totalWidth: number
  rightBorderStyles: string
}) {
  const subHeaders = columns.map((column, index) => {
    const columnWidthPercent = column.widthPercent || 25
    return (
      <div
        key={`sub-header-header-cell-${index}`}
        className={`lg:border-text-primary/15 text-text-primary font-inter hidden items-center truncate text-sm font-bold lg:flex lg:border-l lg:text-[15px]  ${rightBorderStyles}`}
        style={{
          flex: `${(columnWidthPercent / totalWidth) * 100}`,
        }}
      >
        {column.subHeaders.map((subHeader, index) => {
          const widthPercent = subHeader.widthPercent || 50
          return (
            <div
              key={`sub-header-cell-${index}`}
              className={`lg:border-text-primary/15 hidden truncate px-3 first:border-none lg:table-cell lg:border-l`}
              style={{
                flex: `${(widthPercent / totalWidth) * 100}`,
              }}
            >
              <Tooltip
                mode="overflow"
                label={subHeader.title}
                className="flex h-14 w-full items-center justify-center"
                wrapperClassName="justify-center"
              >
                <span className="truncate font-normal">{subHeader.title}</span>
              </Tooltip>
            </div>
          )
        })}
      </div>
    )
  })

  return <div className="bg-background-primary/40 flex items-center">{subHeaders}</div>
}

function TableRows<T extends { id: number | string }, K extends TableDataInterface<T>>({
  columns,
  data,
  totalWidth,
}: {
  columns: TabColumnDefinitionInterface<T, K>[]
  data?: K
  totalWidth: number
}) {
  if (data === undefined) {
    return <TableSkeleton columns={columns} totalWidth={totalWidth} />
  }

  return (
    <>
      {data.items.map((row, index) => {
        return (
          <div
            className="mb-4 flex shrink-0 flex-col gap-2 lg:mb-0 lg:flex-row lg:gap-0"
            key={`row-${index}`}
          >
            {columns.map((column, columnIndex) => {
              const columnHeaderRender =
                column.onHeaderRender && data ? column.onHeaderRender(data) : column.header
              const columnWidthPercent = column.widthPercent || 25
              return (
                <React.Fragment key={`column-parent-header-${columnIndex}`}>
                  {column.subHeaders.length > 0 && (
                    <div className="text-text-primary font-medium lg:hidden">
                      {columnHeaderRender}
                    </div>
                  )}
                  <div
                    key={`sub-header-header-cell-${index}`}
                    className={`lg:border-text-primary/15 flex flex-col truncate lg:flex-row lg:border-b lg:border-l`}
                    style={{
                      flex: `${(columnWidthPercent / totalWidth) * 100}`,
                    }}
                  >
                    {column.subHeaders.map((subHeader, subHeaderIndex) => {
                      const widthPercent = subHeader.widthPercent || 100
                      const onRender = subHeader.onRender
                        ? subHeader.onRender(row)
                        : subHeader.title
                      const onRenderSmall = subHeader.onRenderSmall
                        ? subHeader.onRenderSmall(row)
                        : subHeader.onRender
                        ? subHeader.onRender(row)
                        : subHeader.title
                      const subheaderTitle = subHeader.title ? subHeader.title : column.header
                      const subheaderCalculatedTitle =
                        typeof subheaderTitle === 'string' && !!subheaderTitle.trim()
                          ? `${subheaderTitle} :`
                          : ''

                      const node = (
                        <>
                          <div
                            className={`flex items-center lg:hidden ${
                              !!subheaderCalculatedTitle ? 'gap-1' : ''
                            }`}
                          >
                            {subHeader.onRenderSmall ? (
                              onRenderSmall
                            ) : (
                              <>
                                <span className="text-text-primary/60 whitespace-nowrap lg:font-bold">
                                  {subheaderCalculatedTitle}
                                </span>
                                {onRender}
                              </>
                            )}
                          </div>
                          <div
                            className={`hidden truncate lg:flex ${
                              subHeader.align ? subHeader.align : 'justify-center'
                            } `}
                          >
                            {onRender}
                          </div>
                        </>
                      )

                      return (
                        <div
                          key={`sub-header-cell-${subHeaderIndex}`}
                          className={`lg:border-text-primary/15 flex flex-col truncate first:border-none lg:table-cell lg:border-l lg:px-3 lg:py-3.5`}
                          style={{
                            flex: `${(widthPercent / totalWidth) * 100}`,
                          }}
                        >
                          {node}
                        </div>
                      )
                    })}
                  </div>
                </React.Fragment>
              )
            })}
          </div>
        )
      })}
    </>
  )
}

function TableSkeleton<T extends InstanceWithNestedIdType<T>, K extends TableDataInterface<T>>({
  columns,
  totalWidth,
}: {
  columns: TabColumnDefinitionInterface<T, K>[]
  totalWidth: number
}) {
  return (
    <>
      {Array.from(Array(6).keys()).map((row, index) => (
        <div
          key={`row-${index}`}
          className={`even:bg-brand-primary-subtle odd:bg-background-secondary group flex animate-pulse flex-col items-start gap-4 border border-transparent px-6 py-4 lg:flex-row lg:items-center lg:gap-0 lg:p-0 ${
            index === 5 && 'rounded-b-lg'
          }`}
        >
          {columns.map((column, index2) => {
            if (!column.widthPercent) column.widthPercent = 25
            const widthPercent = column.widthPercent || 25
            return (
              <div
                className={`w-full lg:py-4 lg:pl-2 lg:pr-4`}
                key={`cell-${index2}`}
                style={{
                  flex: `${(widthPercent / totalWidth) * 100}`,
                }}
              >
                <div className="bg-skeleton-primary rounded-sm text-transparent">.</div>
              </div>
            )
          })}
        </div>
      ))}
    </>
  )
}

export default TabTable
